import { cva, type VariantProps } from "class-variance-authority";
import { Avatar as AvatarPrimitive, Separator as SeparatorPrimitive } from "radix-ui";
import type { ComponentProps, ReactNode } from "react";

import { cn } from "../lib/cn";
import { Icon, type IconName } from "./Icon";

// ── Badge / StatusBadge ──────────────────────────────────────────────

export const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap",
  {
    variants: {
      tone: {
        neutral: "bg-foreground/8 text-foreground",
        brand: "bg-brand text-brand-foreground",
        ink: "bg-ink text-ink-foreground",
        success: "bg-success-soft text-success",
        warning: "bg-warning-soft text-warning",
        danger: "bg-danger-soft text-danger",
        info: "bg-info-soft text-info",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

export interface BadgeProps extends ComponentProps<"span">, VariantProps<typeof badgeVariants> {}

export function Badge({ className, tone, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />;
}

export type StatusTone = NonNullable<BadgeProps["tone"]>;

/** Maps workflow states (vendor, product, order, ticket…) to consistent colours everywhere. */
export const STATUS_TONES: Record<string, StatusTone> = {
  active: "success",
  approved: "success",
  published: "success",
  delivered: "success",
  completed: "success",
  paid: "success",
  resolved: "success",
  ready: "success",
  pending: "warning",
  pending_review: "warning",
  under_review: "warning",
  draft: "neutral",
  processing: "info",
  confirmed: "info",
  packed: "info",
  shipped: "info",
  in_progress: "info",
  waiting: "warning",
  changes_requested: "warning",
  open: "info",
  partially_paid: "warning",
  rejected: "danger",
  cancelled: "danger",
  blocked: "danger",
  failed: "danger",
  suspended: "danger",
  expired: "neutral",
  closed: "neutral",
  refunded: "neutral",
};

export interface StatusBadgeProps extends Omit<BadgeProps, "tone" | "children"> {
  status: string;
  /** Translated label; defaults to a readable version of the status code. */
  label?: string;
}

export function StatusBadge({ status, label, ...props }: StatusBadgeProps) {
  const text = label ?? status.replaceAll("_", " ").replace(/^\w/, (c) => c.toUpperCase());
  return (
    <Badge tone={STATUS_TONES[status] ?? "neutral"} {...props}>
      <span className="size-1.5 rounded-full bg-current" aria-hidden />
      {text}
    </Badge>
  );
}

// ── Card ─────────────────────────────────────────────────────────────

export function Card({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "rounded-card border border-border bg-surface text-foreground shadow-card",
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: ComponentProps<"div">) {
  return (
    <div className={cn("flex flex-col gap-1 p-5 pb-0 sm:p-6 sm:pb-0", className)} {...props} />
  );
}

export function CardTitle({ className, ...props }: ComponentProps<"h3">) {
  return <h3 className={cn("text-base font-semibold", className)} {...props} />;
}

export function CardDescription({ className, ...props }: ComponentProps<"p">) {
  return <p className={cn("text-sm text-muted-foreground", className)} {...props} />;
}

export function CardContent({ className, ...props }: ComponentProps<"div">) {
  return <div className={cn("p-5 sm:p-6", className)} {...props} />;
}

export function CardFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-3 border-t border-border px-5 py-4 sm:px-6",
        className,
      )}
      {...props}
    />
  );
}

// ── Alert ────────────────────────────────────────────────────────────

const alertTones = {
  info: { className: "border-info/30 bg-info-soft text-info", icon: "info" },
  success: { className: "border-success/30 bg-success-soft text-success", icon: "circle-check" },
  warning: { className: "border-warning/30 bg-warning-soft text-warning", icon: "alert" },
  danger: { className: "border-danger/30 bg-danger-soft text-danger", icon: "circle-alert" },
} satisfies Record<string, { className: string; icon: IconName }>;

export interface AlertProps extends Omit<ComponentProps<"div">, "title"> {
  tone?: keyof typeof alertTones;
  title?: ReactNode;
}

export function Alert({ tone = "info", title, className, children, ...props }: AlertProps) {
  const config = alertTones[tone];
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      className={cn("flex gap-3 rounded-input border p-3.5 text-sm", config.className, className)}
      {...props}
    >
      <Icon name={config.icon} className="mt-0.5 shrink-0" />
      <div className="space-y-0.5">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className="text-foreground/80">{children}</div>}
      </div>
    </div>
  );
}

// ── Avatar ───────────────────────────────────────────────────────────

export interface AvatarProps {
  name: string;
  src?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const avatarSizes = { sm: "size-8 text-xs", md: "size-10 text-sm", lg: "size-14 text-lg" };

export function initials(name: string): string {
  const parts = name
    .replace(/@.*/, "")
    .split(/[\s._-]+/)
    .filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

export function Avatar({ name, src, size = "md", className }: AvatarProps) {
  return (
    <AvatarPrimitive.Root
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand font-semibold text-brand-foreground",
        avatarSizes[size],
        className,
      )}
    >
      {src && <AvatarPrimitive.Image src={src} alt={name} className="size-full object-cover" />}
      <AvatarPrimitive.Fallback>{initials(name)}</AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
}

// ── Separator / Skeleton / Kbd ───────────────────────────────────────

export function Separator({
  className,
  orientation = "horizontal",
  ...props
}: ComponentProps<typeof SeparatorPrimitive.Root>) {
  return (
    <SeparatorPrimitive.Root
      orientation={orientation}
      className={cn(
        "shrink-0 bg-border",
        orientation === "horizontal" ? "h-px w-full" : "h-full w-px",
        className,
      )}
      {...props}
    />
  );
}

export function Skeleton({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      aria-hidden
      className={cn("animate-pulse rounded-input bg-foreground/8", className)}
      {...props}
    />
  );
}

// ── Price / Rating ───────────────────────────────────────────────────

export interface PriceProps {
  amount: number | string;
  /** Crossed-out MRP shown next to the price. */
  mrp?: number | string | null;
  currency?: string;
  locale?: string;
  className?: string;
}

/** "₹4,999" for whole amounts, "₹4,999.50" when there are paise (like the MOTOLYNK apps). */
export function formatMoney(amount: number | string, currency = "INR", locale = "en-IN"): string {
  const value = Number(amount);
  const digits = Number.isInteger(value) ? 0 : 2;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: 2,
  }).format(value);
}

export function Price({ amount, mrp, currency = "INR", locale = "en-IN", className }: PriceProps) {
  const showMrp = mrp != null && Number(mrp) > Number(amount);
  const discount = showMrp ? Math.round((1 - Number(amount) / Number(mrp)) * 100) : 0;
  return (
    <span className={cn("inline-flex items-baseline gap-2", className)}>
      <span className="font-semibold">{formatMoney(amount, currency, locale)}</span>
      {showMrp && (
        <>
          <s className="text-sm text-muted-foreground">{formatMoney(mrp, currency, locale)}</s>
          <span className="text-xs font-semibold text-danger">{discount}% off</span>
        </>
      )}
    </span>
  );
}

export interface RatingProps {
  value: number;
  count?: number;
  className?: string;
}

export function Rating({ value, count, className }: RatingProps) {
  return (
    <span
      className={cn("inline-flex items-center gap-1 text-sm", className)}
      aria-label={`Rated ${value.toFixed(1)} out of 5`}
    >
      <span aria-hidden className="text-warning">
        ★
      </span>
      <span className="font-semibold">{value.toFixed(1)}</span>
      {count != null && (
        <span className="text-muted-foreground">({count.toLocaleString("en-IN")})</span>
      )}
    </span>
  );
}
